import { DataTablePagination } from '@optools/components/data-table/data-table-pagination';
import { DataTableViewOptions } from '@optools/components/data-table/data-table-view-options';
import { Collapsible, CollapsibleContent } from '@optools/components/ui/collapsible';
import { Input } from '@optools/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { cn } from '@optools/components/ui/utils';
import {
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel as getFilteredRowModelFn,
  getPaginationRowModel as getPaginationRowModelFn,
  getSortedRowModel as getSortedRowModelFn,
  RowSelectionState,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import React, { useState } from 'react';

export type ExpandingDataTableProps<TData> = {
  children?: React.ReactNode;
  className?: string;
  /** Optional row click Handler */
  handleRowClick?: (rowIndex: number) => void;
  /** Shows/hides pagination */
  pagination?: boolean;
  /** Optional selected row  */
  rowSelection?: RowSelectionState;
  /** Shows/hides search input */
  search?: boolean;
  /** Shows/hides view options menu */
  viewOptions?: boolean;
  /** Expandable content */
  renderExpandedRow?: (rowData: TData) => React.ReactNode;
} & Omit<TableOptions<TData>, 'getCoreRowModel'>;

export function ExpandingDataTable<TData>({
  children,
  className,
  pagination = true,
  search = true,
  viewOptions = true,
  ...props
}: ExpandingDataTableProps<TData>) {
  const {
    enableRowSelection,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    handleRowClick,
    initialState,
    onGlobalFilterChange,
    onSortingChange,
    renderExpandedRow,
    rowSelection = {},
    state,
    ...restOfOptions
  } = props;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState<string | undefined>();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useReactTable({
    enableRowSelection,
    filterFromLeafRows: true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel ?? getFilteredRowModelFn(),
    getPaginationRowModel: pagination
      ? (getPaginationRowModel ?? getPaginationRowModelFn())
      : undefined,
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel ?? getSortedRowModelFn(),
    initialState: initialState ?? { ...(pagination && { pagination: { pageSize: 25 } }) },
    onExpandedChange: setExpanded,
    onGlobalFilterChange: onGlobalFilterChange ?? setGlobalFilter,
    onSortingChange: onSortingChange ?? setSorting,
    state: { expanded, globalFilter, rowSelection, sorting, ...state },
    ...restOfOptions,
  });

  return (
    <div className={cn('grid h-full w-full content-start space-y-4 overflow-auto p-1', className)}>
      <div className="flex items-center gap-2">
        {children}
        <div className="flex flex-1 items-center justify-end gap-2">
          {search && (
            <Input
              className="w-full max-w-80"
              placeholder="Search"
              role="searchbox"
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          )}
          {viewOptions && <DataTableViewOptions table={table} />}
        </div>
      </div>

      <div className="flex-1 overflow-hidden rounded-md border">
        <Table containerClassName="h-full relative">
          <TableHeader className="sticky top-0 z-10 border-b bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="px-4">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    {...(enableRowSelection &&
                      handleRowClick && {
                        onClick: () => handleRowClick(row.index),
                      })}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className="py-2 pr-2 pl-4 whitespace-nowrap"
                        style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderExpandedRow && (
                    <TableRow aria-hidden={!row.getIsExpanded()}>
                      <Collapsible asChild open={row.getIsExpanded()}>
                        <td colSpan={row.getAllCells().length}>
                          <CollapsibleContent>{renderExpandedRow(row.original)}</CollapsibleContent>
                        </td>
                      </Collapsible>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell className="h-24 text-center" colSpan={table.getAllColumns().length}>
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && <DataTablePagination table={table} />}
    </div>
  );
}
